import { META_IMAGES, OG_IMAGES } from '@/v2/constants/images/meta'
import { PUBLIC_MAIN_ROUTES } from '@/v2/constants/routes'
import { OGImageType } from '@/v2/types/meta'

export const ogImages: OGImageType[] = [
  {
    url: OG_IMAGES.BASE_URL.concat('/track-your-shipping.jpg'),
    alt: 'track your shipping page og',
    width: 1200,
    height: 630
  }
]

export const metaImages: OGImageType[] = [
  {
    url: META_IMAGES.DEFAULT,
    alt: 'track your shipping page search image',
    width: 92,
    height: 92
  },
  ...ogImages
]

export const heroSectionContent = {
  title: 'Track Your <span>Shipping</span> <span>Anytime & Anywhere</span>',
  description:
    "Easily monitor your shipment's journey with ShipAndStorage. Log in to your account for real-time updates and complete transparency.",
  image: {
    url: '/images/v2/pages/main-pages/track-your-shipping/hero-image.jpg',
    alt: 'track your shipping page'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Track My Shipment' }
}

export const serviceTipsSectionContent = {
  title: 'How to Track Your Shipment',
  description: 'Tracking your shipment with ShipAndStorage is simple and intuitive:',
  ctaButton: { href: PUBLIC_MAIN_ROUTES.BOOK_STORAGE_FACILITY.href, text: 'Find My Order' },

  cards: [
    {
      id: 1,
      title: 'Log In to Your Account',
      description: 'Head over to our login page and enter your credentials. New users have to sign up.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/log-in-to-your-account.svg',
        alt: 'Log In to Your Account'
      }
    },
    {
      id: 2,
      title: 'Access the Tracking Dashboard',
      description: 'Once logged in, navigate to the “Track My Shipment” section for real-time updates.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/access-the-tracking-dashboard.svg',
        alt: 'Access the Tracking Dashboard'
      }
    },
    {
      id: 3,
      title: 'View Shipment Details',
      description: "Get information about your package's location, estimated delivery time, etc.",
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/view-shipment-details.svg',
        alt: 'View Shipment Details'
      }
    }
  ]
}

export const whyWeSectionContent = {
  title: 'Why Track Your Shipment?',
  description: 'Tracking your shipment provides valuable insights and peace of mind throughout the shipping process.',

  cards: [
    {
      id: 1,
      title: 'Stay Informed',
      description: "Receive updates about your shipment's current location and estimated delivery time. ",
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/stay-informed.svg',
        alt: 'Stay Informed'
      }
    },
    {
      id: 2,
      title: 'Enhance Security',
      description:
        'Monitor your shipment’s journey to ensure it is being handled safely and reaches its destination securely.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/enhance-security.svg',
        alt: 'Enhance Security'
      }
    },
    {
      id: 3,
      title: 'Plan Ahead',
      description: 'Use tracking updates to prepare for deliveries and manage your schedule without surprises.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/plan-ahead.svg',
        alt: 'Plan Ahead'
      }
    },
    {
      id: 4,
      title: 'Resolve Issues Quickly',
      description:
        'Address potential delays or unexpected situations promptly with access to accurate tracking information.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/resolve-issues-quickly.svg',
        alt: 'Resolve Issues Quickly'
      }
    }
  ]
}

export const whyChooseShipAndStorageSectionContent = {
  title: 'Why Choose ShipAndStorage?',
  description: 'ShipAndStorage offers unmatched benefits that make tracking and shipping easier than ever:',

  cards: [
    {
      id: 1,
      title: 'User-Friendly Dashboard',
      description:
        'Our platform features an intuitive dashboard that makes navigation simple. You can efficiently track your shipments with ease.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/user-friendly-dashboard.svg',
        alt: 'User-Friendly Dashboard'
      }
    },
    {
      id: 2,
      title: 'Secure Tracking',
      description:
        'We use a robust secure login system to protect your shipment details. This ensures that your information remains confidential and safe.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/secure-tracking.svg',
        alt: 'Secure Tracking'
      }
    },
    {
      id: 3,
      title: 'Comprehensive Features',
      description:
        'Manage multiple shipments seamlessly with our comprehensive features. Update your preferences and access customer support.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/comprehensive-features.svg',
        alt: 'Comprehensive Features'
      }
    },
    {
      id: 4,
      title: 'Reliable Updates',
      description:
        'Receive real-time alerts that keep you informed about your shipments. You’ll always know the latest status and estimated delivery times.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/reliable-updates.svg',
        alt: 'Reliable Updates'
      }
    },
    {
      id: 5,
      title: 'Flexible Integration',
      description:
        'Our system integrates easily with your devices, offering mobile and desktop accessibility for hassle-free tracking.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/flexible-integration.svg',
        alt: 'Flexible Integration'
      }
    },
    {
      id: 6,
      title: 'Dedicated Support',
      description:
        'Our customer support team is available around the clock to assist you with any queries or concerns, ensuring a smooth experience.',
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/dedicated-support.svg',
        alt: 'Dedicated Support'
      }
    }
  ]
}

export const helperSectionContent = {
  title: 'Track with Confidence',
  description:
    'At ShipAndStorage, we prioritize your peace of mind by ensuring a transparent and reliable tracking experience:',

  articles: [
    {
      content: [
        {
          id: 1,
          title: 'Privacy First',
          description: 'Only you can access your shipment details through a secure login system.'
        },
        {
          id: 2,
          title: 'Real-Time Updates',
          description: 'Stay informed with live data about your shipment’s journey'
        },
        {
          id: 3,
          title: 'Real-Time ',
          description: "Log in, check the status, and stay in control—it's that easy."
        }
      ],
      image: {
        src: '/images/v2/pages/main-pages/track-your-shipping/track-with-confidence.jpg',
        alt: 'Track with Confidence'
      }
    }
  ]
}

export const promotionalBannerSectionContent = {
  id: 1,
  title: 'Track Your Shipment Status Online',
  description:
    "Track your shipment's progress in real-time. Use our easy tracking tool to stay updated on your shipment's status.",
  image: {
    src: '/images/v2/pages/main-pages/track-your-shipping/promotional-banner-image.svg',
    alt: 'Track Your Shipment Status Online'
  },
  ctaButton: { href: PUBLIC_MAIN_ROUTES.MOVING_BOX_STORAGE.href, text: 'Start Tracking' }
}

export const faqSectionContent = {
  title: 'Frequently Asked Questions',

  questions: [
    {
      id: 1,
      question: 'Can I track my shipment without an account?',
      answer: 'No, tracking requires logging in to ensure your information and shipment details remain secure.'
    },
    {
      id: 2,
      question: 'What if I forget my password?',
      answer: 'Resetting your password is easy—just click “Forgot Password” on the login page.'
    },
    {
      id: 3,
      question: 'Can I track international shipments?',
      answer: 'Yes! Our system provides updates for both domestic and international shipments.'
    },
    {
      id: 4,
      question: 'How accurate are your tracking updates?',
      answer: 'Our advanced system delivers real-time updates with high accuracy to keep you informed.'
    },
    {
      id: 5,
      question: 'Can I track multiple shipments?',
      answer: 'Absolutely! Our dashboard allows you to manage and track multiple shipments with ease.'
    }
  ]
}
